import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { GripVertical, X, Plus, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/utils/fileSize'
import type { DocumentRecord } from '@/types/catalog'
import DocumentPreview from '@/routes/IntelligentBoard/DocumentPreviewShape/PdfPreview'

interface ReviewGroupModalProps {
  isOpen: boolean
  onClose: () => void
  documents: DocumentRecord[]
  availableDocuments: DocumentRecord[]
  onConfirm: (orderedDocIds: string[]) => Promise<void>
}

export const ReviewGroupModal: React.FC<ReviewGroupModalProps> = ({
  isOpen,
  onClose,
  documents,
  availableDocuments,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const [orderedDocs, setOrderedDocs] = useState<DocumentRecord[]>([])
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [addPopoverOpen, setAddPopoverOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setOrderedDocs([...documents])
      setSelectedDocId(documents[0]?.id || null)
      setIsSaving(false)
    }
  }, [isOpen, documents])

  const addedDocIds = new Set(orderedDocs.map(d => d.id))
  const filteredAvailable = availableDocuments.filter(d => !addedDocIds.has(d.id))

  const moveUp = (index: number) => {
    if (index === 0) return
    const newDocs = [...orderedDocs]
    const temp = newDocs[index]
    newDocs[index] = newDocs[index - 1]
    newDocs[index - 1] = temp
    setOrderedDocs(newDocs)
  }

  const moveDown = (index: number) => {
    if (index === orderedDocs.length - 1) return
    const newDocs = [...orderedDocs]
    const temp = newDocs[index]
    newDocs[index] = newDocs[index + 1]
    newDocs[index + 1] = temp
    setOrderedDocs(newDocs)
  }

  const removeDoc = (docId: string) => {
    const newDocs = orderedDocs.filter(d => d.id !== docId)
    setOrderedDocs(newDocs)
    if (selectedDocId === docId) {
      setSelectedDocId(newDocs[0]?.id || null)
    }
  }

  const addDoc = (doc: DocumentRecord) => {
    setOrderedDocs(prev => [...prev, doc])
    setAddPopoverOpen(false)
  }

  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      await onConfirm(orderedDocs.map(d => d.id))
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        className="max-w-5xl w-[90vw] h-[80vh] flex flex-col p-0"
        hideDefaultClose
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <DialogTitle className="text-lg font-semibold">
              {t('data_catalog:graph_editor.multi_page_set.review_group_title')} ({orderedDocs.length})
            </DialogTitle>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-dark-text-muted" />
          </button>
        </div>

        {/* Body — two columns */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel — file list */}
          <div className="w-[400px] flex flex-col border-r" ref={containerRef}>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {orderedDocs.map((doc, index) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDocId(doc.id)}
                  className={cn(
                    "flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-dark-muted",
                    selectedDocId === doc.id ? "border-2 border-purple-500" : "border border-gray-200 dark:border-dark-border"
                  )}
                >
                  <GripVertical className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium truncate text-gray-900 dark:text-dark-text"
                    >
                      {doc.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                      {formatFileSize(doc.file_size)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); moveUp(index) }}
                      disabled={index === 0}
                      className="h-7 w-7"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); moveDown(index) }}
                      disabled={index === orderedDocs.length - 1}
                      className="h-7 w-7"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeDoc(doc.id)
                      }}
                      className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-dark-muted transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Add button */}
              {filteredAvailable.length > 0 && (
                <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
                    >
                      <Plus className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <span className="text-sm text-gray-500 dark:text-dark-text-muted">
                        {t('data_catalog:graph_editor.multi_page_set.add_pnid')}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[360px] p-0" align="start" side="bottom" container={containerRef.current}>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredAvailable.map(doc => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => addDoc(doc)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors text-left border-b last:border-b-0 cursor-pointer"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-gray-900 dark:text-dark-text">
                              {doc.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                              {formatFileSize(doc.file_size)}
                            </p>
                          </div>
                          <Plus className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Right panel — preview */}
          <div className="flex-1 p-4 flex items-center justify-center min-h-0">
            {selectedDocId ? (
              <div
                className="w-full h-full rounded-lg border-2 border-dashed overflow-hidden flex items-center justify-center border-purple-300 dark:border-purple-700"
              >
                <DocumentPreview
                  docId={selectedDocId}
                  key={selectedDocId}
                />
              </div>
            ) : (
              <div className="text-sm text-gray-400 dark:text-gray-500">
                {t('data_catalog:graph_editor.multi_page_set.no_document_selected', { defaultValue: 'No document selected' })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('common:buttons.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={orderedDocs.length < 2 || isSaving}
            className="bg-indigo-800 hover:bg-indigo-900 text-white"
          >
            {t('data_catalog:graph_editor.multi_page_set.create_group')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ReviewGroupModal
